import * as angular from 'angular';
import * as _ from 'lodash';
import { assign, keyBy, mapValues } from 'lodash';

import { DEFAULT_DIVERGENT_PALETTE, PIVOT_LIMIT } from '../../../common/constants';
import { assignWith, partialRight } from 'lodash';

export var defaults = <any>partialRight(assignWith, (objVal, srcVal) => (objVal ? objVal : srcVal));

export const ChartInterpolaterService = function () {
  let ranges = {};

  function numberTypes(column) {
    return column.type === 'number' || column.type === 'integer' || column.type === 'float';
  }

  function nonNumberTypes(column) {
    return column.type !== 'number' && column.type !== 'integer' && column.type !== 'float';
  }

  function dataTypes(column) {
    return (
      column.type === 'number' ||
      column.type === 'integer' ||
      column.type === 'float' ||
      column.type === 'datetime' ||
      column.type === 'date'
    );
  }

  function dateTypes(column) {
    return column.type === 'datetime' || column.type === 'date';
  }

  function stringTypes(column) {
    return column.type === 'string';
  }

  function setIfEmpty(setting, defaultValue) {
    const emptyValues = [undefined, null, ''];

    if (emptyValues.includes(setting)) {
      return defaultValue;
    } else {
      return setting;
    }
  }

  function initReturnTypes(options, numbers, datetime, totalCols) {
    const returnTypes = [];
    if (_.size(numbers) >= 1) {
      returnTypes.push(options.types.bar);
    }
    const withDatetime = datetime ? 1 : 0;
    if (_.size(numbers) + withDatetime >= 2) {
      returnTypes.push(options.types.line);
      returnTypes.push(options.types.area);
      returnTypes.push(options.types.scatter);
    } else if (_.size(numbers) >= 1 && totalCols >= 2) {
      returnTypes.push(options.types.line);
      returnTypes.push(options.types.area);
    }
    if (totalCols > 0) {
      returnTypes.push(options.types.bar);
      returnTypes.push(options.types.line);
      returnTypes.push(options.types.area);
      returnTypes.push(options.types.pie);
      returnTypes.push(options.types.bigNumber);
      returnTypes.push(options.types.pivotTable);
    }
    if (_.size(numbers) > 1) {
      returnTypes.push(options.types.linePlusBar);
    }
    return returnTypes;
  }

  function initChartLabels(view, label) {
    if (label && label.name) {
      view.bar.x = setIfEmpty(view.bar.x, label.name);
      view.line.label = setIfEmpty(view.line.label, label.name);
      view.linePlusBar.label = setIfEmpty(view.linePlusBar.label, label.name);
      view.area.label = setIfEmpty(view.area.label, label.name);
      view.scatter.label = setIfEmpty(view.scatter.label, label.name);
      view.pie.label = setIfEmpty(view.pie.label, label.name);
    }
  }

  function initDatetimeCharts(view, datetime, dataSize, returnTypes) {
    if (datetime && datetime.name) {
      if (!view.selectedChart && _.find(returnTypes, { type: 'line' }) && dataSize > 4) {
        const chartType: any = _.find(returnTypes, { type: 'line' });
        view.selectedChart = chartType.type;
        view.chartIcon = chartType.icon;
      }
      view.bar.x = setIfEmpty(view.bar.x, datetime.name);
      view.line.x = setIfEmpty(view.line.x, datetime.name);
      view.linePlusBar.x = setIfEmpty(view.linePlusBar.x, datetime.name);
      view.area.x = setIfEmpty(view.area.x, datetime.name);
      view.scatter.x = setIfEmpty(view.scatter.x, datetime.name);
    }
  }

  function initChartsFromFirstNumber(view, numbers) {
    if (!view.bar.x) {
      view.bar.x = setIfEmpty(view.bar.x, numbers[0].name);
      if (!numbers[1]) {
        // if there isnt another number column set x and y to the same;
        view.bar.y = view.bar.y ? view.bar.y : [numbers[0].name];
      }
    } else {
      view.bar.y = view.bar.y ? view.bar.y : [numbers[0].name];
    }
    if (view.line.x) {
      view.line.y = view.line.y ? view.line.y : [numbers[0].name];
    }
    if (view.area.x) {
      view.area.y = view.area.y ? view.area.y : [numbers[0].name];
    }
    if (view.scatter.x) {
      view.scatter.y = view.scatter.y ? view.scatter.y : [numbers[0].name];
      if (!angular.isArray(view.scatter.y)) {
        view.scatter.y = [view.scatter.y];
      }
    }
  }

  function initChartsWithNumbers(view, numbers, nonNumbers, strings) {
    if (numbers && numbers[0]) {
      initChartsFromFirstNumber(view, numbers);

      view.line.x = setIfEmpty(view.line.x, numbers[0].name);
      view.linePlusBar.x = setIfEmpty(view.linePlusBar.x, numbers[0].name);
      view.area.x = setIfEmpty(view.area.x, numbers[0].name);
      view.scatter.x = setIfEmpty(view.scatter.x, numbers[0].name);
      view.pie.value = setIfEmpty(view.pie.value, numbers[0].name);
      view.pie.label = setIfEmpty(view.pie.label, numbers[0].name);
    }

    if (numbers && numbers[1]) {
      view.bar.y = view.bar.y ? view.bar.y : [numbers[1].name];
      view.line.y = view.line.y ? view.line.y : [numbers[1].name];
      view.area.y = view.area.y ? view.area.y : [numbers[1].name];
      view.scatter.y = view.scatter.y ? view.scatter.y : [numbers[1].name];
      if (!angular.isArray(view.scatter.y)) {
        view.scatter.y = [view.scatter.y];
      }
      if (!(view.linePlusBar.ybars && view.linePlusBar.ylines)) {
        view.linePlusBar.ybars = numbers[0].name;
        view.linePlusBar.ylines = numbers[1].name;
      }
      view.linePlusBar.y = [view.linePlusBar.ybars, view.linePlusBar.ylines];
    } else if (!view.line.y && _.size(numbers) === 1 && _.size(nonNumbers)) {
      view.line.y = view.line.y ? view.line.y : [numbers[0].name];
      view.line.x = nonNumbers[0].name;
      view.area.y = view.area.y ? view.area.y : [numbers[0].name];
      view.area.x = nonNumbers[0].name;
    }
    if (view.scatter && view.scatter.y && !angular.isArray(view.scatter.y)) {
      view.scatter.y = [view.scatter.y];
    }

    if ((!numbers || !_.size(numbers)) && _.size(strings)) {
      view.bar.x = setIfEmpty(view.bar.x, strings[0].name);
      view.bar.y = undefined;
      view.bar.aggregate = 'count';
      view.line.x = setIfEmpty(view.line.x, strings[0].name);
      view.line.y = undefined;
      view.line.aggregate = 'count';
      view.area.x = setIfEmpty(view.area.x, strings[0].name);
      view.area.y = undefined;
      view.area.aggregate = 'count';
    }
  }

  function initPieChart(view, numbers, strings, data: any) {
    if (!numbers.length) {
      // we need to do some kind of pie chart aggregate, ideally on string column
      if (strings && strings.length) {
        view.pie.aggregate = setIfEmpty(view.pie.aggregate, 'count');
        view.pie.value = setIfEmpty(view.pie.value, strings[0].name);
        view.pie.label = setIfEmpty(view.pie.label, strings[0].name);
      } else if (_.size(data.columns) > 0) {
        view.pie.aggregate = setIfEmpty(view.pie.aggregate, 'count');
        view.pie.value = setIfEmpty(view.pie.value, (<any>data.columns[0]).name);
        view.pie.label = setIfEmpty(view.pie.label, (<any>data.columns[0]).name);
      }
    } else if (data.count >= 20) {
      view.pie.aggregate = setIfEmpty(view.pie.aggregate, 'sum');
    }
  }

  function initBigNumber(view, numbers: any, data) {
    view.bigNumber.trendLabel = setIfEmpty(view.bigNumber.trendLabel, '');
    view.bigNumber.trendFormat = setIfEmpty(view.bigNumber.trendFormat, 'auto');
    view.bigNumber.trendPrecision = setIfEmpty(view.bigNumber.trendPrecision, 'auto');
    view.bigNumber.colorScaleID = setIfEmpty(view.bigNumber.colorScaleID, 'ggr');
    view.bigNumber.aggregator = setIfEmpty(view.bigNumber.aggregator, 'last_value');
    view.bigNumber.trend = setIfEmpty(view.bigNumber.trend, 'change_rate');
    if (numbers.length) {
      view.bigNumber.column = setIfEmpty(view.bigNumber.column, (<any>numbers[0]).name);
    }
    view.bigNumber.referencePoint = setIfEmpty(view.bigNumber.referencePoint, 'previous');
    view.bigNumber.referencePointCustom = setIfEmpty(view.bigNumber.referencePointCustom, '');
    view.bigNumber.sortColumn = setIfEmpty(view.bigNumber.sortColumn, '');
    view.bigNumber.sortOrder = setIfEmpty(view.bigNumber.sortOrder, 'asc');
    view.bigNumber.columns = setIfEmpty(view.bigNumber.columns, {});
    view.bigNumber.colors = setIfEmpty(view.bigNumber.colors, DEFAULT_DIVERGENT_PALETTE);
  }

  /**
   * @todo add a filterValues procedure to ChartSanitizerService
   * @param view
   * @param numbers
   * @param strings
   * @param data
   */
  function initPivotTable(view, numbers, strings, data) {
    const defaultOpts = {
      aggregator: 'sum',
      columns: [],
      filters: [],
      filterValues: {},
      columnFormats: {},
      format: {},
      rows: [],
      values: [],
    };

    // temporarily disable interpolating columns, rows and values
    // @todo re-implement when we can identify system thresholds for columns, filters, etc...
    //
    // if (strings.length) {
    //   assign(defaultOpts, {
    //     columns: [strings[0].name],
    //     rows: (strings[1]) ? [strings[1].name] : [],
    //   });
    // }
    //
    // if (numbers.length) {
    //   assign(defaultOpts, {
    //     values: [numbers[0].name],
    //   });
    // }

    // create column map with empty array
    assign(
      defaultOpts.filterValues,
      mapValues(keyBy(data.columns, 'name'), (o) => [])
    );

    defaults(view.pivotTable, defaultOpts);
    defaults(view.pivotTable.filterValues, defaultOpts.filterValues);
  }

  return {
    interpolate: function (view, data, options) {
      if (!view && !data) {
        return;
      }
      //reset memoized maxmin ranges
      ranges = {};

      view.bar = view.bar || {};
      view.bigNumber = view.bigNumber || {};
      view.line = view.line || {};
      view.linePlusBar = view.linePlusBar || {};
      view.area = view.area || {};
      view.scatter = view.scatter || {};
      view.pie = view.pie || {};
      view.pivotTable = view.pivotTable || {};

      let label = _.find(data.columns, stringTypes);
      label = setIfEmpty(label, data.columns.length ? data.columns[0] : null);
      const numbers = _.filter(data.columns, numberTypes);
      const datetime = _.find(data.columns, dateTypes);
      const nonNumbers = _.filter(data.columns, nonNumberTypes);
      const strings = _.filter(data.columns, stringTypes);
      const returnTypes = options ? initReturnTypes(options, numbers, datetime, _.size(data.columns)) : [];

      initChartLabels(view, label);

      initDatetimeCharts(view, datetime, data.content.length, returnTypes);

      initChartsWithNumbers(view, numbers, nonNumbers, strings);

      initPieChart(view, numbers, strings, data);

      initBigNumber(view, numbers, data);

      initPivotTable(view, numbers, strings, data);

      if (data.content.length > PIVOT_LIMIT) {
        view.bar.aggregate = view.bar.aggregate || 'sum';
        view.line.aggregate = view.line.aggregate || 'sum';
        view.linePlusBar.aggregate = view.linePlusBar.aggregate || 'sum';
        view.area.aggregate = view.area.aggregate || 'sum';
        view.pie.aggregate = view.pie.aggregate || 'sum';
        // scatter charts have no pivot
      }

      this.setRange(view.bar, data.content, true);
      this.setRange(view.line, data.content, true);
      this.setRange(view.linePlusBar, data.content, true);
      this.setRange(view.area, data.content, true);
      this.setRange(view.scatter, data.content, true);
      return returnTypes;
    },

    setRange: function (view, data, dataChanged) {
      // the first time this is called, memoize everything.
      // subsequent times don't need to change anything,
      // except when data changes.

      // store: yminFromData, ymaxFromData, yminTotal, ymaxTotal, xminFromData, xmaxFromData

      let allValues, totals;

      const memoizeRange = function (cols) {
        let positiveTotals = new Array(data.length),
          negativeTotals = new Array(data.length),
          currentValue;

        allValues = [];
        totals = [];

        cols = angular.isArray(cols) ? cols : [cols];

        angular.forEach(data, function (row, index) {
          angular.forEach(cols, function (col) {
            currentValue = row[col];
            allValues.push(currentValue);
            if (currentValue > 0) {
              if (positiveTotals[index]) {
                positiveTotals[index] += currentValue;
              } else {
                positiveTotals[index] = currentValue;
              }
            } else {
              if (negativeTotals[index]) {
                negativeTotals[index] += currentValue;
              } else {
                negativeTotals[index] = currentValue;
              }
            }
          });
        });

        totals = _.filter(_.uniq(positiveTotals).concat(_.uniq(negativeTotals)), function (item) {
          return !isNaN(item);
        });
        allValues = _.filter(allValues, function (item) {
          return !isNaN(item);
        });
      };

      if (view && view.y && (dataChanged || !angular.isDefined(view.yminFromData))) {
        memoizeRange(view.y);
        if (allValues && allValues.length) {
          view.yminFromData = _.min(allValues);
          view.ymaxFromData = _.max(allValues);
          view.yminTotal = _.min(totals);
          view.ymaxTotal = _.max(totals);
        }
      }

      if (view && view.x && (dataChanged || !angular.isDefined(view.xminFromData))) {
        memoizeRange(view.x);
        if (allValues && allValues.length) {
          view.xminFromData = _.min(allValues);
          view.xmaxFromData = _.max(allValues);
        }
      }
    },
  };
};
